import React, {useEffect, useRef, useState} from "react";
import {connect} from "react-redux";
import {FormattedMessage, useIntl} from "react-intl";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";

import {downloadImageWithFetch} from "../../../../misc/misc2";
import LookBeforeAfterSlider from "./LookBeforeAfterSlider";
import {loadImagePromise} from "../../../../misc/offlineImagesCache";
import LoadingSpinner from "../../../ui/loadingSpinner";
import LookAIMultiImage from "./LookAIMultiImage";
import LookAILoadingBar from "./LookAILoadingBar";
import AIResultCustomPromptButtons from "./AIResultCustomPromptButtons";
import AIResultCustomPrompt, {AI_EDIT_BLOCK_HEIGHT} from "./AIResultCustomPrompt";
import {AiEditIcon} from "../../../common/svgIcons/svgIcons";
import {getPieceGridPosition} from "../../aiCutImageIntoPieces";
import {setLookAIEditPiece} from "../../aiActions";

const EDIT_TRANSITION_MS = 350; // keep in sync with .look-ai__overlay-image-area / -edit-image transitions

// pieceIndex of lookAIEditPiece when the WHOLE result is being edited (every result that is not
// a multiImage one): there is no 2x2 grid to cut, so there is no piece - the image posted to the
// edit-image endpoint is the result image itself.
const WHOLE_IMAGE_PIECE_INDEX = null;

const isWholeImageEdit = (pieceIndex) => (pieceIndex === null) || (pieceIndex === undefined);

// Geometry of the zoomed piece: it starts as the very quadrant it was cut from (50% x 50%,
// placed in its own corner) and expands from there to the whole image area. The whole-image
// edit has nothing to zoom out of - it covers the whole area from the first frame.
const getEditImageStyle = (pieceIndex, expanded) => {
  if (expanded || isWholeImageEdit(pieceIndex)) {
    return {left: "0%", top: "0%", width: "100%", height: "100%"};
  }
  const {row, column} = getPieceGridPosition(pieceIndex);
  return {
    left: (column * 50) + "%",
    top: (row * 50) + "%",
    width: "50%",
    height: "50%",
  };
}

const LookAIImageOverlayImageContent = (props) => {

  const {aiResults, aiUserFileBlobUrl, opened, closeAIImageOverlay, lookAIEditPiece,
    aiRegenerateLoading, setLookAIEditPiece} = props;
  const [imagesLoaded, setImagesLoaded] = useState([]);
  // the image area has no height of its own (the <img> sizes it), so opening the custom prompt
  // block locks the current height first - only then can it transition down to (height - 80px).
  const [lockedAreaHeight, setLockedAreaHeight] = useState(0);
  // false right after the piece is picked (it is still sitting in its own corner), true once
  // the expand transition has been kicked off - also drives the image area / edit block, so
  // everything animates at the same time
  const [editExpanded, setEditExpanded] = useState(false);
  const imageAreaRef = useRef(null);

  const intl = useIntl();

  const activeResult = aiResults.find(aiResult => aiResult.isActive);
  const imageLoaded = activeResult ? imagesLoaded.includes(activeResult.resultImageUrl) : false;
  // a "multiImage" result is one image built out of 4 variants laid out 2x2, see aiApi
  const isMultiImage = !!(activeResult && activeResult.isMultiImage);

  // what is currently opened for a custom prompt edit, in either of its two modes:
  // viewOnly (a piece of a multiImage result was clicked - before/after slider stays on top of
  // it) or the custom prompt mode (its "edit" button was clicked - the prompt block slides in,
  // and only THEN is the slider hidden). For every other result the whole image is what gets
  // edited (pieceIndex === WHOLE_IMAGE_PIECE_INDEX), and it is always the custom prompt mode.
  const openedPiece = activeResult && lookAIEditPiece && lookAIEditPiece.dataUrl
    && (lookAIEditPiece.resultId === activeResult.id) ? lookAIEditPiece : null;
  const editing = !!openedPiece;
  const promptEditing = editing && !openedPiece.viewOnly;

  // the typed prompt is deliberately kept - the user gets it back when re-opening the edit mode
  const closeEditMode = () => {
    setEditExpanded(false);
    setLookAIEditPiece(null);
  }

  useEffect(() => {
    const loaded = activeResult ? imagesLoaded.includes(activeResult.resultImageUrl) : false;
    if (!loaded) {
      const loadImages = async () => {
        await loadImagePromise(activeResult.resultImageUrl);
        setImagesLoaded([...imagesLoaded, activeResult.resultImageUrl]);
      }
      loadImages();
    }
  }, [imagesLoaded, activeResult]);

  useEffect(() => {
    if (!editing || editExpanded) {
      return;
    }
    // two frames: the first one mounts the piece over its own quadrant, the second one starts
    // the transition to the full area (a style applied in the mounting frame would not animate)
    const firstFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setEditExpanded(true));
    });
    return () => window.cancelAnimationFrame(firstFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // switching to another result (or generating a new one) always drops the edit mode
  useEffect(() => {
    if (lookAIEditPiece && (!activeResult || lookAIEditPiece.resultId !== activeResult.id)) {
      closeEditMode();
      setLockedAreaHeight(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeResult, lookAIEditPiece]);

  // Opens what the custom prompt will be applied to. For a multiImage result that is one of the
  // pre-cut pieces (see LookAIMultiImage - the cutting happens up front, so there is nothing to
  // await here): viewOnly=true came from a click on the piece itself, viewOnly=false from its
  // "edit" button; going from the first to the second keeps the piece exactly where it is and
  // only reveals the prompt block underneath. For every other result pieceIndex is
  // WHOLE_IMAGE_PIECE_INDEX and dataUrl is the result image itself.
  const onPieceOpen = (pieceIndex, dataUrl, viewOnly) => {
    if (!activeResult || !dataUrl) {
      return;
    }
    if (!viewOnly && !lockedAreaHeight && imageAreaRef.current) {
      setLockedAreaHeight(imageAreaRef.current.offsetHeight); // lock it before it shrinks
    }
    setLookAIEditPiece({
      resultId: activeResult.id,
      pieceIndex,
      dataUrl,
      viewOnly: !!viewOnly,
      regenerateError: "",
    });
  }

  // the "edit" button next to "download" - a non-multiImage result is edited as a whole
  const onWholeImageEditClick = () => {
    onPieceOpen(WHOLE_IMAGE_PIECE_INDEX, activeResult.resultImageUrl, false);
  }

  const onEditRevertClick = () => {
    setEditExpanded(false); // collapses the piece back into its corner, grows the image area back
    window.setTimeout(() => {
      setLookAIEditPiece(null); // the typed prompt stays, only the zoom is dropped
      setLockedAreaHeight(0); // back to the natural (auto) height
    }, EDIT_TRANSITION_MS + 50);
  }

  if (!activeResult) {
    return null;
  }

  const imageAreaStyle = lockedAreaHeight
    ? {height: promptEditing && editExpanded
        ? Math.max(lockedAreaHeight - AI_EDIT_BLOCK_HEIGHT, 0)
        : lockedAreaHeight}
    : undefined;

  const sourceImageUrl = activeResult.sourceImageUrl || aiUserFileBlobUrl;

  return (
    <div className="flex--1 relative u-w-full u-h-full u-rounded-025 overflow-hidden">
      <div ref={imageAreaRef}
           style={imageAreaStyle}
           className="relative u-w-full look-ai__overlay-image-area">
        <picture>
          {/*<source srcSet={activeResult.resultImageUrl} media="(min-width: 600px)"/>*/}
          <img className={imageLoaded ? "" : "opacity-0 visibility-hidden"}
               src={activeResult.resultImageUrl} alt="Your Generated Design"/>
        </picture>
        {/* the slider is only in the way of the edit mode - in every other state (including a
            piece opened by clicking it) it stays where it is. For a multiImage result it is
            pushed under the pieces grid, so only its drag handle takes clicks. */}
        {imageLoaded ? (isMultiImage || editing || (activeResult.resultImageUrl && !activeResult.sourceImageUrl)
          ? null : <LookBeforeAfterSlider
            opened={opened}
            backgroundImageUrl={sourceImageUrl}
            overlayImageUrl={activeResult.resultImageUrl}
          />) : (
          <div
            className="u-rounded-025 absolute u-left-0 u-top-0 u-w-full u-h-full overflow-hidden flex flex--center">
            <LoadingSpinner/>
          </div>
        )}
        {editing ? (
          <div className="look-ai__overlay-edit-image"
               style={getEditImageStyle(openedPiece.pieceIndex, editExpanded)}>
            <img src={openedPiece.dataUrl} alt="Your Generated Design"/>
            {/* clicking a piece is a "look closer" mode - the comparison with the original
                photo is exactly what it is for, so the slider is rendered on the piece too */}
            {openedPiece.viewOnly && editExpanded && sourceImageUrl ? (
              <LookBeforeAfterSlider opened={editExpanded}
                                     className={"look-ai__overlay-slider--under-pieces"}
                                     backgroundImageUrl={sourceImageUrl}
                                     overlayImageUrl={openedPiece.dataUrl}/>
            ) : null}
            {openedPiece.viewOnly ? (
              <AIResultCustomPromptButtons pieceIndex={openedPiece.pieceIndex}
                                           className="look-ai__overlay-edit-image-buttons"
                                           onEditClick={() => onPieceOpen(openedPiece.pieceIndex,
                                             openedPiece.dataUrl, false)}/>
            ) : null}
            {aiRegenerateLoading ? (
              <div className="absolute u-left-0 u-w-full bottom-24 flex flex--center z-10 pointer-events-none">
                <LookAILoadingBar className="look-ai__overlay-edit-loading-bar"/>
              </div>
            ) : null}
          </div>
        ) : null}
        {isMultiImage && imageLoaded && !editing
          ? <LookAIMultiImage resultImageUrl={activeResult.resultImageUrl}
                              onPieceOpen={onPieceOpen}/> : null}
      </div>
      <AIResultCustomPrompt opened={promptEditing && editExpanded}/>
      {/* a multiImage result carries its edit buttons on the pieces themselves; every other one
          gets "edit" as a plain text button right before "download", styled the same way. Both
          are hidden while the prompt block is open - it takes over the bottom of the overlay. */}
      {isMultiImage || editing ? null : (
        <div className="look-ai__overlay-actions flex u-gap-05 u-right-1 u-bottom-1">
          {activeResult.resultImageUrl ? (
            <Button className="look-ai__overlay-download-button u-pl-2 u-pr-2 nowrap"
                    startIcon={AiEditIcon}
                    onClick={onWholeImageEditClick}>
              <FormattedMessage id="look.ai.overlay.editImageButton" defaultMessage="Edit"/>
            </Button>
          ) : null}
          <Button className="look-ai__overlay-download-button u-pl-2 u-pr-2 nowrap"
                  onClick={() => downloadImageWithFetch(activeResult.resultImageUrl, "visualizer.png")}>
            <FormattedMessage id="look.ai.overlay.downloadImageButton"/>
          </Button>
        </div>
      )}
      <IconButton aria-label={intl.formatMessage({id: editing ? "iconButton.back" : "iconButton.close"})}
                  className="look-icon look-icon--small look-icon--white2 rounded-full absolute z-20 u-top-075 u-right-075"
                  onClick={editing ? onEditRevertClick : closeAIImageOverlay}>
        {editing ? <ReplayIcon/> : <CloseIcon/>}
      </IconButton>
    </div>
  )

};

const mapStateToProps = (store) => {
  const {lookOverlayOpened} = store.lookState;
  const {aiUserFileBlobUrl, aiResults, lookAIEditPiece, aiRegenerateLoading} = store.aiState;

  return {
    aiResults,
    lookOverlayOpened,
    aiUserFileBlobUrl,
    lookAIEditPiece,
    aiRegenerateLoading,
  };
};

const mapDispatchToProps = {
  setLookAIEditPiece,
};


export default connect(mapStateToProps, mapDispatchToProps)(LookAIImageOverlayImageContent)
